/**
 * Document requirements configuration for UK Workboat Code Edition 3
 * Defines which policies and procedures are required based on vessel profile
 */

/**
 * Document types in the SMS
 */
export const DOC_TYPES = {
  // Core Policies (Always Required)
  SAFETY_POLICY: 'safety_policy',
  MASTERS_AUTHORITY: 'masters_authority',
  DPA: 'designated_person_ashore',
  DRUG_ALCOHOL: 'drug_alcohol',
  
  // Operational Procedures
  EMERGENCY_PROCEDURES: 'emergency_procedures',
  MAINTENANCE_PROCEDURES: 'maintenance_procedures',
  NAVIGATION_PROCEDURES: 'navigation_procedures',
  CARGO_PROCEDURES: 'cargo_procedures',
  PASSENGER_PROCEDURES: 'passenger_procedures',
  
  // Risk Management
  RISK_ASSESSMENT: 'risk_assessment',
  PERMIT_TO_WORK: 'permit_to_work',
  MANAGEMENT_OF_CHANGE: 'management_of_change',
  
  // Environmental
  MARPOL_PROCEDURES: 'marpol_procedures',
  SOPEP: 'sopep',
  WASTE_MANAGEMENT: 'waste_management',
  
  // Training & Familiarisation
  TRAINING_MATRIX: 'training_matrix',
  FAMILIARISATION_PROCEDURES: 'familiarisation_procedures',
  
  // Reporting
  INCIDENT_REPORTING: 'incident_reporting',
  NON_CONFORMITY: 'non_conformity',
  AUDIT_PROCEDURES: 'audit_procedures',
  
  // Communications
  EMERGENCY_CONTACTS: 'emergency_contacts',
  REPORTING_PROCEDURES: 'reporting_procedures',
  
  // Vessel Specific
  STABILITY_BOOK: 'stability_book',
  FIRE_PLAN: 'fire_plan',
  LSA_PLAN: 'lsa_plan'
};

/**
 * Review frequencies (days)
 */
export const REVIEW_FREQUENCIES = {
  ANNUAL: 365,
  BIENNIAL: 730,
  TRIENNIAL: 1095,
  CONTINUOUS: 0 // Reviewed continuously, no fixed schedule
};

/**
 * Get required documents based on vessel profile
 */
export const getRequiredDocuments = (vesselProfile) => {
  const required = [];
  
  // Core policies - always required for all vessels
  required.push({
    type: DOC_TYPES.SAFETY_POLICY,
    name: 'Company Safety Policy',
    description: 'Company commitment to safety and environmental protection',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 5.1',
    mandatory: true
  });
  
  required.push({
    type: DOC_TYPES.MASTERS_AUTHORITY,
    name: "Master's Authority & Responsibility",
    description: "Master's overriding authority for safety and environmental decisions",
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 5.2',
    mandatory: true
  });
  
  required.push({
    type: DOC_TYPES.DPA,
    name: 'Designated Person Ashore (DPA)',
    description: 'Contact details and responsibilities of the DPA',
    reviewFrequency: REVIEW_FREQUENCIES.CONTINUOUS,
    regulatory: 'WBC3 Section 5.3',
    mandatory: true
  });
  
  required.push({
    type: DOC_TYPES.DRUG_ALCOHOL,
    name: 'Drug & Alcohol Policy',
    description: 'Company policy on drug and alcohol use',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 5.4',
    mandatory: true
  });
  
  // Emergency procedures - required for all
  required.push({
    type: DOC_TYPES.EMERGENCY_PROCEDURES,
    name: 'Emergency Procedures',
    description: 'Response procedures for emergency situations',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 10',
    mandatory: true
  });
  
  // Maintenance - required for all
  required.push({
    type: DOC_TYPES.MAINTENANCE_PROCEDURES,
    name: 'Maintenance Procedures',
    description: 'Planned maintenance system and procedures',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 11',
    mandatory: true
  });
  
  // Navigation procedures - required for operational vessels
  if (vesselProfile.category <= 5) {
    required.push({
      type: DOC_TYPES.NAVIGATION_PROCEDURES,
      name: 'Navigation Procedures',
      description: 'Safe navigation and watchkeeping procedures',
      reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
      regulatory: 'WBC3 Section 19',
      mandatory: true
    });
  }
  
  // Cargo procedures - if cargo operations
  if (vesselProfile.operations?.includes('cargo')) {
    required.push({
      type: DOC_TYPES.CARGO_PROCEDURES,
      name: 'Cargo Operations Procedures',
      description: 'Safe cargo handling and securing procedures',
      reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
      regulatory: 'WBC3 Section 24',
      mandatory: true
    });
  }
  
  // Passenger procedures - if passenger vessel
  if (vesselProfile.hasPassengers || vesselProfile.vesselType === 'pax') {
    required.push({
      type: DOC_TYPES.PASSENGER_PROCEDURES,
      name: 'Passenger Safety Procedures',
      description: 'Passenger briefing and safety management',
      reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
      regulatory: 'WBC3 Section 25',
      mandatory: true
    });
  }
  
  // Environmental - MARPOL applicable
  if (vesselProfile.category <= 3 || vesselProfile.tonnage > 400) {
    required.push({
      type: DOC_TYPES.MARPOL_PROCEDURES,
      name: 'MARPOL Procedures',
      description: 'Pollution prevention procedures',
      reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
      regulatory: 'MARPOL Annex I-VI',
      mandatory: true
    });
  }
  
  // SOPEP - if required
  if (vesselProfile.SOPEP_applicable || vesselProfile.oilCapacity > 400) {
    required.push({
      type: DOC_TYPES.SOPEP,
      name: 'Shipboard Oil Pollution Emergency Plan',
      description: 'Response plan for oil pollution incidents',
      reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
      regulatory: 'MARPOL Annex I',
      mandatory: true
    });
  }
  
  // Risk assessment - always required
  required.push({
    type: DOC_TYPES.RISK_ASSESSMENT,
    name: 'Risk Assessment Procedures',
    description: 'Risk assessment methodology and records',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 6',
    mandatory: true
  });
  
  // Training - always required
  required.push({
    type: DOC_TYPES.FAMILIARISATION_PROCEDURES,
    name: 'Familiarisation & Training Procedures',
    description: 'Crew familiarisation and ongoing training',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 7',
    mandatory: true
  });
  
  // Incident reporting - always required
  required.push({
    type: DOC_TYPES.INCIDENT_REPORTING,
    name: 'Incident Reporting Procedures',
    description: 'Reporting and investigation of incidents',
    reviewFrequency: REVIEW_FREQUENCIES.ANNUAL,
    regulatory: 'WBC3 Section 12',
    mandatory: true
  });
  
  // Audit procedures - always required
  required.push({
    type: DOC_TYPES.AUDIT_PROCEDURES,
    name: 'Audit & Review Procedures',
    description: 'Internal audit and management review procedures',
    reviewFrequency: REVIEW_FREQUENCIES.TRIENNIAL,
    regulatory: 'WBC3 Section 13',
    mandatory: true
  });
  
  // Plans - vessel specific
  required.push({
    type: DOC_TYPES.FIRE_PLAN,
    name: 'Fire Control Plan',
    description: 'Location of fire fighting equipment and escape routes',
    reviewFrequency: REVIEW_FREQUENCIES.CONTINUOUS,
    regulatory: 'WBC3 Section 15',
    mandatory: true
  });
  
  if (vesselProfile.category <= 3) {
    required.push({
      type: DOC_TYPES.LSA_PLAN,
      name: 'Life Saving Appliances Plan',
      description: 'Location and operation of LSA equipment',
      reviewFrequency: REVIEW_FREQUENCIES.CONTINUOUS,
      regulatory: 'WBC3 Section 14',
      mandatory: true
    });
  }
  
  return required;
};

/**
 * Get document categories for organization
 */
export const DOC_CATEGORIES = {
  POLICIES: {
    name: 'Policies',
    description: 'Company policies and commitments',
    types: [
      DOC_TYPES.SAFETY_POLICY,
      DOC_TYPES.MASTERS_AUTHORITY,
      DOC_TYPES.DPA,
      DOC_TYPES.DRUG_ALCOHOL
    ]
  },
  PROCEDURES: {
    name: 'Procedures',
    description: 'Operational and safety procedures',
    types: [
      DOC_TYPES.EMERGENCY_PROCEDURES,
      DOC_TYPES.MAINTENANCE_PROCEDURES,
      DOC_TYPES.NAVIGATION_PROCEDURES,
      DOC_TYPES.CARGO_PROCEDURES,
      DOC_TYPES.PASSENGER_PROCEDURES,
      DOC_TYPES.RISK_ASSESSMENT,
      DOC_TYPES.PERMIT_TO_WORK,
      DOC_TYPES.FAMILIARISATION_PROCEDURES,
      DOC_TYPES.INCIDENT_REPORTING,
      DOC_TYPES.AUDIT_PROCEDURES
    ]
  },
  ENVIRONMENTAL: {
    name: 'Environmental',
    description: 'Environmental protection documentation',
    types: [
      DOC_TYPES.MARPOL_PROCEDURES,
      DOC_TYPES.SOPEP,
      DOC_TYPES.WASTE_MANAGEMENT
    ]
  },
  PLANS: {
    name: 'Plans & Drawings',
    description: 'Vessel plans and arrangements',
    types: [
      DOC_TYPES.STABILITY_BOOK,
      DOC_TYPES.FIRE_PLAN,
      DOC_TYPES.LSA_PLAN
    ]
  }
};

/**
 * Check if a document is overdue for review
 */
export const isOverdueForReview = (lastReviewDate, frequencyDays) => {
  if (!lastReviewDate || frequencyDays === 0) return false;
  
  const lastReview = new Date(lastReviewDate);
  const nextDue = new Date(lastReview);
  nextDue.setDate(nextDue.getDate() + frequencyDays);
  
  return new Date() > nextDue;
};

/**
 * Get next review date
 */
export const getNextReviewDate = (lastReviewDate, frequencyDays) => {
  if (!lastReviewDate || frequencyDays === 0) return null;
  
  const lastReview = new Date(lastReviewDate);
  const nextDue = new Date(lastReview);
  nextDue.setDate(nextDue.getDate() + frequencyDays);
  
  return nextDue.toISOString();
};